import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LuChevronRight, LuPanelLeftClose, LuPanelLeftOpen, LuX } from 'react-icons/lu';
import { NAV_ITEMS, isPathActive } from '../config/navigation';
import logo from '../assets/logo.png';
import { cn } from '../utils/cn';

// Delay before the collapsed rail expands on hover, so a passing pointer doesn't open it
const HOVER_DELAY_MS = 120;
const DESKTOP_QUERY = '(min-width: 1024px)';
// Matches the group's `duration-200` height animation below
const GROUP_ANIMATION_MS = 200;
const GROUP_SCROLL_PADDING = 8;

const isGroupActive = (item, location) =>
    Boolean(item.children?.some((child) => isPathActive(child.path, location)));

const findActiveGroup = (location) => NAV_ITEMS.find((item) => isGroupActive(item, location));

const ITEM_BASE =
    'group flex h-10 w-full items-center gap-3 rounded-lg px-3.5 text-sm font-medium transition-colors duration-150';
const ITEM_IDLE =
    'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white';
const ICON_IDLE = 'text-slate-400 group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300';
const ICON_ACTIVE = 'text-brand-600 dark:text-brand-400';

function Sidebar({ collapsed, onToggleCollapsed, mobileOpen, onCloseMobile }) {
    const location = useLocation();
    const [hovered, setHovered] = useState(false);
    const hoverTimer = useRef(null);
    // Groups work as an accordion: opening one closes the other, so the menu stays short and the
    // opened group slides up into the space the closed one leaves (both heights animate together)
    const [openGroup, setOpenGroup] = useState(() => findActiveGroup(location)?.label ?? null);
    const navRef = useRef(null);
    const groupRefs = useRef({});

    // Navigating into a group opens it (and closes any other)
    useEffect(() => {
        const active = findActiveGroup(location);
        if (active) setOpenGroup(active.label);
    }, [location]);

    // Once a group has finished opening, scroll the menu just enough to show all of it,
    // without ever pushing the group's own heading out of view
    useEffect(() => {
        if (!openGroup) return undefined;
        const timer = setTimeout(() => {
            const nav = navRef.current;
            const group = groupRefs.current[openGroup];
            if (!nav || !group) return;
            const view = nav.getBoundingClientRect();
            const box = group.getBoundingClientRect();
            let delta = 0;
            if (box.top < view.top) {
                delta = box.top - view.top - GROUP_SCROLL_PADDING;
            } else if (box.bottom > view.bottom) {
                delta = Math.min(box.bottom - view.bottom + GROUP_SCROLL_PADDING, box.top - view.top - GROUP_SCROLL_PADDING);
            }
            if (delta !== 0) {
                const instant = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                nav.scrollBy({ top: delta, behavior: instant ? 'auto' : 'smooth' });
            }
        }, GROUP_ANIMATION_MS + 20);
        return () => clearTimeout(timer);
    }, [openGroup]);

    useEffect(() => () => clearTimeout(hoverTimer.current), []);

    // On desktop, labels show when the sidebar is pinned open or the rail is hovered.
    // The mobile drawer always shows labels (the `lg:` prefixes below only apply on desktop).
    const expanded = !collapsed || hovered;
    const labelClass = cn('truncate transition-opacity duration-150', !expanded && 'lg:opacity-0');

    const handleMouseEnter = () => {
        if (!collapsed || !window.matchMedia(DESKTOP_QUERY).matches) return;
        clearTimeout(hoverTimer.current);
        hoverTimer.current = setTimeout(() => setHovered(true), HOVER_DELAY_MS);
    };

    const handleMouseLeave = () => {
        clearTimeout(hoverTimer.current);
        setHovered(false);
    };

    const handleToggleCollapsed = () => {
        setHovered(false);
        onToggleCollapsed();
    };

    const toggleGroup = (label) => setOpenGroup((current) => (current === label ? null : label));

    return (
        <>
            {/* Mobile backdrop */}
            <div
                aria-hidden="true"
                onClick={onCloseMobile}
                className={cn(
                    'fixed inset-0 z-40 bg-slate-900/40 transition-opacity duration-200 lg:hidden dark:bg-slate-950/70',
                    mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
                )}
            />

            <aside
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={cn(
                    'fixed inset-y-0 left-0 z-50 flex w-72 flex-col border-r border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900',
                    'transition-[transform,width,box-shadow] duration-200 ease-out lg:translate-x-0',
                    mobileOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full',
                    expanded ? 'lg:w-64' : 'lg:w-[72px]',
                    collapsed && hovered ? 'lg:shadow-xl' : 'lg:shadow-none'
                )}
            >
                {/* Brand */}
                <div className="flex h-16 shrink-0 items-center gap-3 border-b border-slate-100 px-[18px] dark:border-slate-800">
                    <Link
                        to="/dashboard"
                        aria-label="Go to dashboard"
                        className="shrink-0 rounded-xl transition-transform duration-200 hover:scale-105"
                    >
                        <img src={logo} alt="" className="h-9 w-9 rounded-xl shadow-sm shadow-brand-600/30" />
                    </Link>
                    <button
                        type="button"
                        onClick={onCloseMobile}
                        aria-label="Close menu"
                        className="ml-auto inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 lg:hidden dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
                    >
                        <LuX className="h-5 w-5" />
                    </button>
                </div>

                {/* Menu */}
                <nav ref={navRef} aria-label="Main" className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-3 py-4">
                    <p
                        className={cn(
                            'px-3.5 pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500',
                            labelClass
                        )}
                    >
                        Menu
                    </p>
                    <ul className="space-y-1">
                        {NAV_ITEMS.map((item) => {
                            const Icon = item.icon;

                            if (!item.children) {
                                const active = isPathActive(item.path, location);
                                return (
                                    <li key={item.label}>
                                        <Link
                                            to={item.path}
                                            aria-current={active ? 'page' : undefined}
                                            className={cn(
                                                ITEM_BASE,
                                                active ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300' : ITEM_IDLE
                                            )}
                                        >
                                            <Icon aria-hidden="true" className={cn('h-5 w-5 shrink-0', active ? ICON_ACTIVE : ICON_IDLE)} />
                                            <span className={labelClass}>{item.label}</span>
                                        </Link>
                                    </li>
                                );
                            }

                            const groupActive = isGroupActive(item, location);
                            const open = openGroup === item.label;

                            return (
                                <li key={item.label} ref={(el) => { groupRefs.current[item.label] = el; }}>
                                    <button
                                        type="button"
                                        aria-expanded={open}
                                        onClick={() => toggleGroup(item.label)}
                                        className={cn(
                                            ITEM_BASE,
                                            groupActive
                                                ? 'text-slate-900 hover:bg-slate-100 dark:text-white dark:hover:bg-slate-800'
                                                : ITEM_IDLE,
                                            groupActive && !expanded && 'lg:bg-brand-50 dark:lg:bg-brand-500/10'
                                        )}
                                    >
                                        <Icon aria-hidden="true" className={cn('h-5 w-5 shrink-0', groupActive ? ICON_ACTIVE : ICON_IDLE)} />
                                        <span className={labelClass}>{item.label}</span>
                                        <LuChevronRight
                                            aria-hidden="true"
                                            className={cn(
                                                'ml-auto h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 dark:text-slate-500',
                                                open && 'rotate-90',
                                                !expanded && 'lg:opacity-0'
                                            )}
                                        />
                                    </button>

                                    {/* grid-rows 0fr -> 1fr animates the height without measuring it */}
                                    <div
                                        className={cn(
                                            'grid transition-[grid-template-rows] duration-200 ease-out',
                                            open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                                            !expanded && 'lg:hidden'
                                        )}
                                    >
                                        <div className="overflow-hidden">
                                            <ul className="ml-[23px] mt-1 space-y-0.5 border-l border-slate-200 pb-1 pl-3 dark:border-slate-800">
                                                {item.children.map((child) => {
                                                    const active = isPathActive(child.path, location);
                                                    return (
                                                        <li key={child.path}>
                                                            <Link
                                                                to={child.path}
                                                                tabIndex={open ? undefined : -1}
                                                                aria-current={active ? 'page' : undefined}
                                                                className={cn(
                                                                    'relative flex h-9 items-center rounded-lg px-3 text-sm transition-colors duration-150',
                                                                    active
                                                                        ? 'bg-brand-50 font-medium text-brand-700 before:absolute before:-left-[13px] before:bottom-2 before:top-2 before:w-0.5 before:rounded-full before:bg-brand-600 dark:bg-brand-500/10 dark:text-brand-300 dark:before:bg-brand-400'
                                                                        : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-white'
                                                                )}
                                                            >
                                                                <span className="truncate">{child.label}</span>
                                                            </Link>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </nav>

                {/* Collapse toggle (desktop) */}
                <div className="hidden shrink-0 border-t border-slate-100 p-3 lg:block dark:border-slate-800">
                    <button
                        type="button"
                        onClick={handleToggleCollapsed}
                        className={cn(
                            ITEM_BASE,
                            'text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white'
                        )}
                    >
                        {collapsed ? (
                            <LuPanelLeftOpen className="h-5 w-5 shrink-0" aria-hidden="true" />
                        ) : (
                            <LuPanelLeftClose className="h-5 w-5 shrink-0" aria-hidden="true" />
                        )}
                        <span className={labelClass}>{collapsed ? 'Keep sidebar open' : 'Collapse sidebar'}</span>
                    </button>
                </div>
            </aside>
        </>
    );
}

export default Sidebar;
